import type { Color } from '@/types/Color'
import type { UUID } from '@/types/Id'

// region: read

export type ExtractionStatus =
  | 'extracted'
  | 'not_extracted'
  | 'failed'
  | 'running'

export const ENTITY_TYPES = [
  'file',
  'person',
  'agency',
  'company',
  'department',
  'catalog_entry',
] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

export const ENTITY_AGENCY_JURISDICTION_TYPES = [
  'federal',
  'state',
  'county',
  'municipality',
  'special_district',
  'tribal',
  'territory',
  'education',
  'regional',
  'other',
] as const
export type EntityAgencyJurisdictionType =
  (typeof ENTITY_AGENCY_JURISDICTION_TYPES)[number]

export const ENTITY_AGENCY_JURISDICTION_TYPE_OPTIONS: EntityAgencyJurisdictionType[] =
  [...ENTITY_AGENCY_JURISDICTION_TYPES]

export const ENTITY_AGENCY_TYPE_OPTIONS: string[] = [
  'Administration',
  'Agency',
  'Airport Authority',
  'Authority',
  'Borough Government',
  'Board',
  'Branch',
  'Bridge Authority',
  'Bureau',
  'Center',
  'Charter School',
  'City Government',
  'Commission',
  'Community College',
  'Community Services District',
  'Conservation District',
  'Constitutional Office',
  'Cooperative Purchasing Organization',
  'Correctional Facility',
  'Council',
  'Council of Governments',
  'County Government',
  'Department',
  'District',
  'Division',
  'Drainage District',
  'Economic Development Authority',
  'Emergency Medical Services',
  'Emergency Management',
  'Fire Department',
  'Fire District',
  'Flood Control District',
  'Government',
  'Health System',
  'Highway Authority',
  'Hospital',
  'Hospital District',
  'Housing Authority',
  'Inspector General',
  'Institute',
  'Irrigation District',
  'Jail',
  'Joint Powers Authority',
  'Laboratory',
  'Law Enforcement',
  'Legislature',
  'Library District',
  'Library System',
  'Metropolitan Planning Organization',
  'Military Command',
  'Mosquito Control District',
  'Municipal Government',
  'Nonprofit Instrumentality',
  'Office',
  'Other',
  'Ombudsman',
  'Parish Government',
  'Park District',
  'Planning Agency',
  'Police Department',
  'Port Authority',
  'Power Authority',
  'Procurement Office',
  'Program',
  'Public Corporation',
  'Public Health Department',
  'Public School',
  'Public Works',
  'Purchasing Cooperative',
  'Quasi-Governmental Organization',
  'Regional Planning Organization',
  'Redevelopment Authority',
  'Research Center',
  'School District',
  'Service',
  'Sheriff Office',
  'Soil and Water Conservation District',
  'Special District',
  'State Government',
  'Town Government',
  'Township Government',
  'Toll Authority',
  'Transit Authority',
  'Transportation Department',
  'University',
  'University System',
  'Utility',
  'Utility District',
  'Village Government',
  'Water Authority',
  'Water District',
  'Workforce Board',
]

export interface EntityRelationship {
  relationship_type: string
  predicate: string
  to_entity_id: UUID
  from_entity_id: UUID
}

export interface RelationshipWithEntities extends EntityRelationship {
  from_entity?: Entity | null
  to_entity?: Entity | null
}

export interface EntityBase {
  id: UUID
  entity_type: EntityType
  created_at: string
  last_modified: string
  last_referenced_at: string | null
  organization_id: UUID
  created_by_user_id?: UUID | null
  confirmed_by_user_id: UUID | null
  confirmed: boolean
  description: string | null
  extended_schema_id: UUID | null
  extended_data: Record<string, unknown> | null
  owner_user_id: UUID | null
  priority: string | null
  relationship_status: RelationshipStatus | null
  last_contacted_at: string | null
  tag_ids: UUID[]
  relationships?: EntityRelationship[] | null

  // archived information
  archived: boolean
  archived_at: string | null
  archived_by: UUID | null
}

export interface EntityFile extends EntityBase {
  entity_type: 'file'
  file_id: UUID
}

export interface EntityPerson extends EntityBase {
  entity_type: 'person'
  first_name: string
  middle_name: string | null
  last_name: string
  preferred_name: string | null
  suffixes: string[] | null
  aliases: string[] | null
  role: string | null
  email: string | null
  alternate_email: string | null
  phone: string | null
  mobile_phone: string | null
  office_phone: string | null
  linkedin_url: string | null
  resume_file_id: UUID | null
  location_id: UUID | null
  user_id: UUID | null
  entity_company_id: UUID | null
  entity_agency_id?: UUID | null
  entity_department_id: UUID | null
  is_internal: boolean
  affiliations: EntityPersonAffiliation[]
}

export interface EntityAgency extends EntityBase {
  entity_type: 'agency'
  name: string
  short_name: string | null
  abbreviation: string | null
  acronym: string | null
  aliases: string[] | null
  parent_entity_agency_id: UUID | null
  jurisdiction_type: EntityAgencyJurisdictionType | null
  agency_type: string | null
  agency_level: string | null
  official_website_url: string | null
  procurement_url: string | null
  vendor_registration_url: string | null
  general_email: string | null
  general_phone: string | null
  location_id: UUID | null
}

export interface EntityCompany extends EntityBase {
  entity_type: 'company'
  name: string
  legal_name: string | null
  dba_name: string | null
  aliases: string[] | null
  industry: string | null
  relationship_types: string[] | null
  uei: string | null
  cage_code: string | null
  website_url: string | null
  linkedin_url: string | null
  parent_entity_company_id: UUID | null
  company_id: UUID | null
  location_id: UUID | null
  linked_organization_id: UUID | null
  company_size: string | null
  socioeconomic_categories: string[] | null
  naics_codes: string[] | null
  psc_codes: string[] | null
  vehicles: string[] | null
  sam_registration_status: string | null
  sam_expiration_date: string | null
  facility_clearance_level: string | null
  capabilities_summary: string | null
  core_capabilities: string[] | null
  primary_contact_entity_person_id: UUID | null
}

export interface EntityCompanyDepartment extends EntityBase {
  entity_type: 'department'
  name: string
  entity_company_id: UUID
  parent_department_id: UUID | null
}

export interface EntityPersonAffiliation {
  id: UUID
  created_at: string
  last_modified: string
  organization_id: UUID
  person_id: UUID
  entity_company_id: UUID | null
  entity_agency_id: UUID | null
  entity_department_id: UUID | null
  is_primary: boolean
  started_at: string | null
  ended_at: string | null
}

export interface EntityExternalReference {
  id: UUID
  created_at: string
  last_modified: string
  organization_id: UUID
  entity_id: UUID
  source: string
  external_object_type: string
  external_id: string
  external_url: string | null
  created_by_user_id: UUID | null
  last_synced_at: string | null
  metadata: Record<string, unknown> | null
}

export interface EntityCatalogEntry extends EntityBase {
  entity_type: 'catalog_entry'
  name: string
}

export type Entity =
  | EntityAgency
  | EntityCatalogEntry
  | EntityCompany
  | EntityCompanyDepartment
  | EntityFile
  | EntityPerson

export interface EntitySuggestion {
  entity: Entity
}

export interface EntityCountResponse {
  type: EntityType
  count: number
}

export interface EntitySchemaRow {
  id: UUID
  organization_id: UUID
  name: string
  version: number
  color: string
  entity_type: EntityType
  json_schema: Record<string, unknown>
}

// endregion: read

// region: create

export interface CreateEntityCompany {
  entity_type: 'company'
  name: string
  legal_name: string | null
  dba_name?: string | null
  aliases?: string[] | null
  industry: string | null
  description: string | null
  relationship_types?: string[] | null
  uei?: string | null
  cage_code?: string | null
  website_url?: string | null
  linkedin_url?: string | null
  parent_entity_company_id?: UUID | null
  linked_organization_id?: UUID | null
  relationship_status?: string | null
  owner_user_id?: UUID | null
  priority?: string | null
  last_contacted_at?: string | null
  company_size?: string | null
  socioeconomic_categories?: string[] | null
  naics_codes?: string[] | null
  psc_codes?: string[] | null
  vehicles?: string[] | null
  sam_registration_status?: string | null
  sam_expiration_date?: string | null
  facility_clearance_level?: string | null
  capabilities_summary?: string | null
  core_capabilities?: string[] | null
  primary_contact_entity_person_id?: UUID | null
  tag_ids?: UUID[] | null
  // location_id: UUID | null
}

export interface CreateEntityPerson {
  entity_type: 'person'
  first_name: string
  middle_name?: string | null
  last_name: string
  preferred_name?: string | null
  suffixes: string[] | null
  aliases?: string[] | null
  role: string | null
  email: string | null
  alternate_email?: string | null
  phone: string | null
  mobile_phone?: string | null
  office_phone?: string | null
  linkedin_url?: string | null
  entity_company_id: UUID | null
  entity_agency_id?: UUID | null
  entity_department_id: UUID | null
  resume_file_id: UUID | null
  description: string | null
  user_id: UUID | null
  is_internal?: boolean
  relationship_status?: string | null
  owner_user_id?: UUID | null
  priority?: string | null
  last_contacted_at?: string | null
  tag_ids?: UUID[] | null
  // location_id: UUID | null
}

export interface CreateEntityDepartment {
  entity_type: 'department'
  name: string
  entity_company_id: UUID
  parent_department_id: UUID | null
  description: string | null
}

export interface CreateEntityAgency {
  entity_type: 'agency'
  name: string
  short_name?: string | null
  abbreviation?: string | null
  acronym?: string | null
  aliases?: string[] | null
  parent_entity_agency_id?: UUID | null
  jurisdiction_type?: EntityAgencyJurisdictionType | null
  agency_type?: string | null
  agency_level?: string | null
  official_website_url?: string | null
  procurement_url?: string | null
  vendor_registration_url?: string | null
  general_email?: string | null
  general_phone?: string | null
  description?: string | null
  location_id?: UUID | null
  relationship_status?: string | null
  owner_user_id?: UUID | null
  priority?: string | null
  last_contacted_at?: string | null
  tag_ids?: UUID[] | null
}

export interface CreateEntityCatalogEntry {
  entity_type: 'catalog_entry'
  name: string
  description: string | null
  extended_schema_id: UUID | null
  extended_data: Record<string, unknown> | null
}

// endregion: create

// region: update

export interface UpdateEntityCompany {
  entity_type: 'company'
  name: string | null
  legal_name: string | null
  dba_name?: string | null
  aliases?: string[] | null
  industry: string | null
  description: string | null
  relationship_types: string[] | null
  uei: string | null
  cage_code: string | null
  website_url: string | null
  linkedin_url?: string | null
  parent_entity_company_id?: UUID | null
  linked_organization_id?: UUID | null
  relationship_status?: string | null
  owner_user_id?: UUID | null
  priority?: string | null
  last_contacted_at?: string | null
  company_size?: string | null
  socioeconomic_categories?: string[] | null
  naics_codes?: string[] | null
  psc_codes?: string[] | null
  vehicles?: string[] | null
  sam_registration_status?: string | null
  sam_expiration_date?: string | null
  facility_clearance_level?: string | null
  capabilities_summary?: string | null
  core_capabilities?: string[] | null
  primary_contact_entity_person_id?: UUID | null
  tag_ids?: UUID[] | null
  // location_id: UUID | null
}

export interface UpdateEntityPerson {
  entity_type: 'person'
  first_name: string | null
  middle_name?: string | null
  last_name: string | null
  preferred_name?: string | null
  suffixes: string[] | null
  aliases?: string[] | null
  role: string | null
  email: string | null
  alternate_email?: string | null
  phone: string | null
  mobile_phone?: string | null
  office_phone?: string | null
  linkedin_url?: string | null
  entity_company_id: UUID | null
  entity_agency_id?: UUID | null
  entity_department_id: UUID | null
  resume_file_id: UUID | null
  description: string | null
  is_internal?: boolean | null
  relationship_status?: string | null
  owner_user_id?: UUID | null
  priority?: string | null
  last_contacted_at?: string | null
  tag_ids?: UUID[] | null
  // location_id: UUID | null
}

export interface UpdateEntityDepartment {
  entity_type: 'department'
  name: string | null
  parent_department_id: UUID | null
  description: string | null
}

export interface UpdateEntityAgency {
  entity_type: 'agency'
  name?: string | null
  short_name?: string | null
  abbreviation?: string | null
  acronym?: string | null
  aliases?: string[] | null
  parent_entity_agency_id?: UUID | null
  jurisdiction_type?: EntityAgencyJurisdictionType | null
  agency_type?: string | null
  agency_level?: string | null
  official_website_url?: string | null
  procurement_url?: string | null
  vendor_registration_url?: string | null
  general_email?: string | null
  general_phone?: string | null
  description?: string | null
  location_id?: UUID | null
  relationship_status?: string | null
  owner_user_id?: UUID | null
  priority?: string | null
  last_contacted_at?: string | null
  tag_ids?: UUID[] | null
}

// endregion: update

// region: field metadata for UI

export type FieldType =
  | 'text'
  | 'url'
  | 'email'
  | 'phone'
  | 'date'
  | 'textarea'
  | 'dropdown'
  | 'combobox'
  | 'multi_combobox'
  | 'hidden'
  | 'company_reference'
  | 'person_reference'
  | 'agency_reference'
  | 'department_reference'
  | 'location_reference'
  | 'file_reference'
  | 'tag_multi'
  | 'string_array'
  | 'tag_input'
  | 'comma_separated_array'

export type EntityDateFieldMode = 'date' | 'datetime'

export interface SimpleFieldMetadata {
  label: string
  type: Exclude<FieldType, 'dropdown' | 'combobox' | 'multi_combobox'>
  editable: boolean
  required: boolean
  placeholder?: string
  dateMode?: EntityDateFieldMode
}

/** Dropdown with enforced options - user must select from the list */
export interface DropdownFieldMetadata {
  label: string
  type: 'dropdown'
  editable: boolean
  required: boolean
  placeholder?: string
  options: string[]
  optionLabels?: Record<string, string>
  optionColors?: Record<string, Color>
}

/** Combobox with suggested options - user can search or enter custom value */
export interface ComboboxFieldMetadata {
  label: string
  type: 'combobox'
  editable: boolean
  required: boolean
  placeholder?: string
  options: string[]
  optionColors?: Record<string, Color>
}

/** Multi-select combobox for array fields with suggested options */
export interface MultiComboboxFieldMetadata {
  label: string
  type: 'multi_combobox'
  editable: boolean
  required: boolean
  placeholder?: string
  options: string[]
  optionDescriptions?: Record<string, string>
  optionColors?: Record<string, Color>
  allowCustom?: boolean
}

export type FieldMetadata =
  | SimpleFieldMetadata
  | DropdownFieldMetadata
  | ComboboxFieldMetadata
  | MultiComboboxFieldMetadata

// default relationship status options
export const RELATIONSHIP_STATUS_OPTIONS = [
  // Early pipeline
  { value: 'new', label: 'New', color: 'gray-light' },
  { value: 'prospect', label: 'Prospect', color: 'slate-light' },
  { value: 'contacted', label: 'Contacted', color: 'blue-light' },
  { value: 'engaged', label: 'Engaged', color: 'purple-light' },
  // Active states
  { value: 'active', label: 'Active', color: 'green-light' },
  { value: 'on_hold', label: 'On Hold', color: 'orange-light' },
  // Historical/ended states
  { value: 'past', label: 'Past', color: 'yellow-light' },
  { value: 'inactive', label: 'Inactive', color: 'amber-light' },
  { value: 'do_not_contact', label: 'Do Not Contact', color: 'red-light' },
] as const

export type RelationshipStatus =
  (typeof RELATIONSHIP_STATUS_OPTIONS)[number]['value']

export type RelationshipStatusColor =
  (typeof RELATIONSHIP_STATUS_OPTIONS)[number]['color']

export const RELATIONSHIP_STATUS_OPTION_VALUES: string[] =
  RELATIONSHIP_STATUS_OPTIONS.map((option) => option.value)

export const RELATIONSHIP_STATUS_OPTION_LABELS: Record<
  RelationshipStatus,
  string
> = {
  new: 'New',
  prospect: 'Prospect',
  contacted: 'Contacted',
  engaged: 'Engaged',
  active: 'Active',
  on_hold: 'On Hold',
  past: 'Past',
  inactive: 'Inactive',
  do_not_contact: 'Do Not Contact',
}

export const RELATIONSHIP_STATUS_OPTION_COLORS: Record<
  RelationshipStatus,
  Color
> = {
  new: 'gray-light',
  prospect: 'slate-light',
  contacted: 'blue-light',
  engaged: 'purple-light',
  active: 'green-light',
  on_hold: 'orange-light',
  past: 'yellow-light',
  inactive: 'amber-light',
  do_not_contact: 'red-light',
}

export const ENTITY_PRIORITY_OPTIONS: string[] = [
  'Low',
  'Medium',
  'High',
  'Strategic',
]

export const ENTITY_PRIORITY_OPTION_COLORS: Record<string, Color> = {
  Low: 'zinc-light',
  Medium: 'blue-light',
  High: 'amber-light',
  Strategic: 'purple-light',
}

// Centralized options for company relationship dropdowns
export const COMPANY_RELATIONSHIP_OPTIONS: string[] = [
  'Affiliate',
  'Authorized Dealer',
  'BPA Team Member',
  'Channel Partner',
  'Competitor',
  'Consortium Member',
  'Consulting Partner',
  'CTA Lead',
  'CTA Member',
  'Customer',
  'Distributor',
  'Former Partner',
  'GWAC/MAC Teaming Partner',
  'IDIQ Prime',
  'IDIQ Subcontractor',
  'Implementation Partner',
  'Inactive',
  'Joint Venture Lead',
  'Joint Venture Member',
  'Joint Venture Partner',
  'Mentor',
  'Not Applicable',
  'OEM',
  'Other',
  'Parent Company',
  'Prime Contractor',
  'Prospect',
  'Protégé',
  'Reseller',
  'Schedule Partner (GSA)',
  'Strategic Alliance',
  'Strategic Partner',
  'Subcontractor',
  'Subsidiary',
  'Supplier',
  'System Integrator',
  'Teaming Partner',
  'Technology Partner',
  'Value-Added Reseller (VAR)',
  'Vendor',
]

export const COMPANY_RELATIONSHIP_OPTION_COLORS: Record<string, Color> = {
  Affiliate: 'slate-light',
  'Authorized Dealer': 'sky-light',
  'BPA Team Member': 'cyan-light',
  'Channel Partner': 'teal-light',
  Competitor: 'red-light',
  'Consortium Member': 'violet-light',
  'Consulting Partner': 'blue-light',
  'CTA Lead': 'purple-light',
  'CTA Member': 'purple-light',
  Customer: 'green-light',
  Distributor: 'orange-light',
  'Former Partner': 'gray-light',
  'GWAC/MAC Teaming Partner': 'cyan-light',
  'IDIQ Prime': 'emerald-light',
  'IDIQ Subcontractor': 'lime-light',
  'Implementation Partner': 'teal-light',
  Inactive: 'gray-light',
  'Joint Venture Lead': 'fuchsia-light',
  'Joint Venture Member': 'pink-light',
  'Joint Venture Partner': 'rose-light',
  Mentor: 'indigo-light',
  'Not Applicable': 'zinc-light',
  OEM: 'stone-light',
  Other: 'zinc-light',
  'Parent Company': 'slate-light',
  'Prime Contractor': 'emerald-light',
  Prospect: 'blue-light',
  Protégé: 'indigo-light',
  Reseller: 'sky-light',
  'Schedule Partner (GSA)': 'cyan-light',
  'Strategic Alliance': 'violet-light',
  'Strategic Partner': 'purple-light',
  Subcontractor: 'lime-light',
  Subsidiary: 'slate-light',
  Supplier: 'orange-light',
  'System Integrator': 'teal-light',
  'Teaming Partner': 'violet-light',
  'Technology Partner': 'blue-light',
  'Value-Added Reseller (VAR)': 'sky-light',
  Vendor: 'amber-light',
}

export const COMPANY_SIZE_OPTIONS: string[] = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1,000',
  '1,001-5,000',
  '5,001-10,000',
  '10,001+',
]

export const COMPANY_SIZE_OPTION_COLORS: Record<string, Color> = {
  '1-10': 'zinc-light',
  '11-50': 'slate-light',
  '51-200': 'blue-light',
  '201-500': 'sky-light',
  '501-1,000': 'cyan-light',
  '1,001-5,000': 'teal-light',
  '5,001-10,000': 'purple-light',
  '10,001+': 'violet-light',
}

export const SOCIOECONOMIC_CATEGORY_OPTIONS: string[] = [
  '8(a)',
  'ANC',
  'EDWOSB',
  'HBCU/MI',
  'HUBZone',
  'Large Business',
  'Native Hawaiian Organization',
  'Nonprofit',
  'SDB',
  'SDVOSB',
  'Small Business',
  'Tribal Enterprise',
  'VOSB',
  'WOSB',
]

export const SOCIOECONOMIC_CATEGORY_OPTION_COLORS: Record<string, Color> = {
  '8(a)': 'violet-light',
  ANC: 'teal-light',
  EDWOSB: 'pink-light',
  'HBCU/MI': 'indigo-light',
  HUBZone: 'blue-light',
  'Large Business': 'slate-light',
  'Native Hawaiian Organization': 'cyan-light',
  Nonprofit: 'green-light',
  SDB: 'purple-light',
  SDVOSB: 'amber-light',
  'Small Business': 'emerald-light',
  'Tribal Enterprise': 'orange-light',
  VOSB: 'yellow-light',
  WOSB: 'rose-light',
}

export const VEHICLE_OPTIONS: string[] = [
  '2GIT',
  'Army CHESS ADMC-3',
  'Army CHESS ITES-3H',
  'Army ITES-3S',
  'Army ITES-4S',
  'Army ITES-SW2',
  'Army RS3',
  'CMS RMADA 2',
  'CMS SPARC',
  'CDAO Tradewinds Solutions Marketplace',
  'Department of State EVOLVE',
  'DHS EAGLE II',
  'DHS EAGLE Next Gen',
  'DHS TABSS',
  'DHA OMNIBUS IV',
  'DIA SITE III',
  'DISA DES',
  'DISA ENCORE III',
  'DISA GSM-O II',
  'DISA SETI',
  'DLA JETS',
  'DLA JETS 2.0',
  'DOJ ITSS-5',
  'E&I Cooperative Services',
  'Equalis Group',
  'FAA eFAST',
  'FAA SAVES',
  'FBI ITSSS',
  'FBI ITSSS-2',
  'GSA 8(a) STARS III',
  'GSA Alliant 2',
  'GSA Alliant 3',
  'GSA Ascend BPA',
  'GSA ASTRO',
  'GSA COMET II',
  'GSA EIS',
  'GSA HCaTS',
  'GSA HCaTS Small Business',
  'GSA MAS',
  'GSA OASIS',
  'GSA OASIS Small Business',
  'GSA OASIS+',
  'GSA Polaris',
  'GSA VETS 2',
  'HGACBuy',
  'MDA SHIELD (Scalable Homeland Innovative Enterprise Layered Defense)',
  'NASA NCAPS',
  'NASA SEWP V',
  'NASA SEWP VI',
  'NASPO ValuePoint',
  'Navy CANES',
  'Navy NGEN-R',
  'NCPA',
  'NETCENTS-2',
  'NIH CIO-CS',
  'NIH CIO-SP3',
  'NIH CIO-SP3 Small Business',
  'NIH CIO-SP4',
  'NOAA NMITS',
  'NOAA ProTech',
  'OMNIA Partners',
  'Responsive Strategic Sourcing for Services (RS3)',
  'SBEAS',
  'SeaPort NxG',
  'SeaPort-e',
  'Sourcewell',
  'The Interlocal Purchasing System (TIPS)',
  'USDA DAITSS',
  'VA T4NG',
  'VA T4NG2',
  'VA VECTOR',
  'VA VECTOR 2.0',
]

export const SAM_REGISTRATION_STATUS_OPTIONS: string[] = [
  'Active',
  'Pending',
  'Expired',
  'Inactive',
  'Not Registered',
  'Unknown',
]

export const SAM_REGISTRATION_STATUS_OPTION_COLORS: Record<string, Color> = {
  Active: 'green-light',
  Pending: 'amber-light',
  Expired: 'red-light',
  Inactive: 'gray-light',
  'Not Registered': 'zinc-light',
  Unknown: 'slate-light',
}

export const FACILITY_CLEARANCE_LEVEL_OPTIONS: string[] = [
  'None',
  'Confidential',
  'Secret',
  'Top Secret',
  'TS/SCI',
  'L Clearance',
  'Q Clearance',
  'Other',
]

export const FACILITY_CLEARANCE_LEVEL_OPTION_COLORS: Record<string, Color> = {
  None: 'zinc-light',
  Confidential: 'blue-light',
  Secret: 'purple-light',
  'Top Secret': 'violet-light',
  'TS/SCI': 'amber-light',
  'L Clearance': 'teal-light',
  'Q Clearance': 'emerald-light',
  Other: 'slate-light',
}

// Ensure all UpdateEntityPerson fields are defined with metadata
export const UPDATE_ENTITY_PERSON_FIELDS: Record<
  keyof Omit<UpdateEntityPerson, 'entity_type'>,
  FieldMetadata
> = {
  first_name: {
    label: 'First Name',
    type: 'text',
    editable: true,
    required: true,
    placeholder: 'e.g. John',
  },
  middle_name: {
    label: 'Middle Name',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. Quincy',
  },
  last_name: {
    label: 'Last Name',
    type: 'text',
    editable: true,
    required: true,
    placeholder: 'e.g. Doe',
  },
  preferred_name: {
    label: 'Preferred Name',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. Johnny',
  },
  suffixes: {
    label: 'Suffix',
    type: 'tag_input',
    editable: true,
    required: false,
    placeholder: 'Add suffix...',
  },
  aliases: {
    label: 'Aliases',
    type: 'tag_input',
    editable: true,
    required: false,
    placeholder: 'Add alias...',
  },
  role: {
    label: 'Title / Role',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. Chief Executive Officer',
  },
  email: {
    label: 'Email',
    type: 'email',
    editable: true,
    required: false,
    placeholder: 'e.g. john.doe@example.com',
  },
  alternate_email: {
    label: 'Alternate Email',
    type: 'email',
    editable: true,
    required: false,
    placeholder: 'e.g. john@example.com',
  },
  phone: {
    label: 'Phone',
    type: 'phone',
    editable: true,
    required: false,
    placeholder: 'e.g. +1 (123) 456-7890',
  },
  mobile_phone: {
    label: 'Mobile Phone',
    type: 'phone',
    editable: true,
    required: false,
    placeholder: 'e.g. +1 (123) 456-7890',
  },
  office_phone: {
    label: 'Office Phone',
    type: 'phone',
    editable: true,
    required: false,
    placeholder: 'e.g. +1 (123) 456-7890',
  },
  linkedin_url: {
    label: 'LinkedIn',
    type: 'url',
    editable: true,
    required: false,
    placeholder: 'e.g. https://linkedin.com/in/johndoe',
  },
  description: {
    label: 'Description',
    type: 'textarea',
    editable: true,
    required: false,
    placeholder: 'Add a short summary...',
  },
  entity_company_id: {
    label: 'Company',
    type: 'company_reference',
    editable: true,
    required: false,
    placeholder: 'e.g. Acme Inc.',
  },
  entity_agency_id: {
    label: 'Agency',
    type: 'agency_reference',
    editable: true,
    required: false,
    placeholder: 'Select agency...',
  },
  entity_department_id: {
    label: 'Department',
    type: 'department_reference',
    editable: true,
    required: false,
    placeholder: 'e.g. Engineering',
  },
  resume_file_id: {
    label: 'Resume',
    type: 'file_reference',
    editable: true,
    required: false,
    placeholder: 'e.g. Resume.pdf',
  },
  is_internal: {
    label: 'Internal Team Member',
    type: 'hidden',
    editable: false,
    required: false,
  },
  relationship_status: {
    label: 'Relationship Status',
    type: 'hidden',
    editable: false,
    required: false,
  },
  owner_user_id: {
    label: 'Owner',
    type: 'hidden',
    editable: false,
    required: false,
  },
  priority: {
    label: 'Priority',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select or type priority',
    options: ENTITY_PRIORITY_OPTIONS,
    optionColors: ENTITY_PRIORITY_OPTION_COLORS,
  },
  last_contacted_at: {
    label: 'Last Contacted',
    type: 'date',
    editable: true,
    required: false,
  },
  tag_ids: {
    label: 'Tags',
    type: 'tag_multi',
    editable: true,
    required: false,
  },
  // location_id: { label: 'Location', type: 'location_reference', editable: true, required: false },
}

// Ensure all UpdateEntityCompany fields are defined with metadata
export const UPDATE_ENTITY_COMPANY_FIELDS: Record<
  keyof Omit<UpdateEntityCompany, 'entity_type'>,
  FieldMetadata
> = {
  name: {
    label: 'Name',
    type: 'text',
    editable: true,
    required: true,
    placeholder: 'e.g. Acme Inc.',
  },
  legal_name: {
    label: 'Legal Name',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. Acme Corporation',
  },
  dba_name: {
    label: 'DBA Name',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. Acme Federal',
  },
  aliases: {
    label: 'Aliases',
    type: 'tag_input',
    editable: true,
    required: false,
    placeholder: 'Add alias...',
  },
  industry: {
    label: 'Primary Industry',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. Technology',
  },
  relationship_types: {
    label: 'Relationship(s)',
    type: 'multi_combobox',
    editable: true,
    required: false,
    placeholder: 'Search relationships...',
    options: COMPANY_RELATIONSHIP_OPTIONS,
    optionColors: COMPANY_RELATIONSHIP_OPTION_COLORS,
    allowCustom: true,
  },
  uei: {
    label: 'UEI',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. ABCDEFGHIJKL',
  },
  cage_code: {
    label: 'CAGE Code',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. 1A2B3',
  },
  website_url: {
    label: 'Website',
    type: 'url',
    editable: true,
    required: false,
    placeholder: 'e.g. https://example.com',
  },
  linkedin_url: {
    label: 'LinkedIn',
    type: 'url',
    editable: true,
    required: false,
    placeholder: 'e.g. https://linkedin.com/company/acme',
  },
  parent_entity_company_id: {
    label: 'Parent Company',
    type: 'company_reference',
    editable: true,
    required: false,
    placeholder: 'Select parent company...',
  },
  description: {
    label: 'Description',
    type: 'textarea',
    editable: true,
    required: false,
    placeholder: 'Add a short summary...',
  },
  linked_organization_id: {
    label: 'Linked Organization',
    type: 'hidden',
    editable: false,
    required: false,
  },
  relationship_status: {
    label: 'Relationship Status',
    type: 'hidden',
    editable: false,
    required: false,
  },
  owner_user_id: {
    label: 'Owner',
    type: 'hidden',
    editable: false,
    required: false,
  },
  priority: {
    label: 'Priority',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select or type priority',
    options: ENTITY_PRIORITY_OPTIONS,
    optionColors: ENTITY_PRIORITY_OPTION_COLORS,
  },
  last_contacted_at: {
    label: 'Last Contacted',
    type: 'date',
    editable: true,
    required: false,
  },
  company_size: {
    label: 'Employee Range',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select company size...',
    options: COMPANY_SIZE_OPTIONS,
    optionColors: COMPANY_SIZE_OPTION_COLORS,
  },
  socioeconomic_categories: {
    label: 'Socioeconomic Status',
    type: 'multi_combobox',
    editable: true,
    required: false,
    placeholder: 'Search socioeconomic categories...',
    options: SOCIOECONOMIC_CATEGORY_OPTIONS,
    optionColors: SOCIOECONOMIC_CATEGORY_OPTION_COLORS,
    allowCustom: true,
  },
  naics_codes: {
    label: 'NAICS Codes',
    type: 'multi_combobox',
    editable: true,
    required: false,
    placeholder: 'Search by code or description...',
    options: [],
    allowCustom: true,
  },
  psc_codes: {
    label: 'PSC Codes',
    type: 'multi_combobox',
    editable: true,
    required: false,
    placeholder: 'Search by code or description...',
    options: [],
    allowCustom: true,
  },
  vehicles: {
    label: 'Contract Vehicles',
    type: 'multi_combobox',
    editable: true,
    required: false,
    placeholder: 'Search vehicles...',
    options: VEHICLE_OPTIONS,
    allowCustom: true,
  },
  sam_registration_status: {
    label: 'SAM Status',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select SAM status...',
    options: SAM_REGISTRATION_STATUS_OPTIONS,
    optionColors: SAM_REGISTRATION_STATUS_OPTION_COLORS,
  },
  sam_expiration_date: {
    label: 'SAM Expiration Date',
    type: 'date',
    editable: true,
    required: false,
    dateMode: 'date',
  },
  facility_clearance_level: {
    label: 'Facility Clearance',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select clearance level...',
    options: FACILITY_CLEARANCE_LEVEL_OPTIONS,
    optionColors: FACILITY_CLEARANCE_LEVEL_OPTION_COLORS,
  },
  capabilities_summary: {
    label: 'Capabilities Summary',
    type: 'textarea',
    editable: true,
    required: false,
    placeholder: 'Summarize relevant capabilities...',
  },
  core_capabilities: {
    label: 'Core Capabilities',
    type: 'tag_input',
    editable: true,
    required: false,
    placeholder: 'Add capability...',
  },
  primary_contact_entity_person_id: {
    label: 'Primary Contact',
    type: 'person_reference',
    editable: true,
    required: false,
    placeholder: 'Select primary contact...',
  },
  tag_ids: {
    label: 'Tags',
    type: 'tag_multi',
    editable: true,
    required: false,
  },
  // location_id: {
  //   label: 'Location',
  //   type: 'location_reference',
  //   editable: true,
  //   required: false,
  // },
}

// Ensure all UpdateEntityDepartment fields are defined with metadata
export const UPDATE_ENTITY_DEPARTMENT_FIELDS: Record<
  keyof Omit<UpdateEntityDepartment, 'entity_type'>,
  FieldMetadata
> = {
  name: {
    label: 'Name',
    type: 'text',
    editable: true,
    required: true,
    placeholder: 'e.g. Procurement',
  },
  parent_department_id: {
    label: 'Parent Department',
    type: 'department_reference',
    editable: true,
    required: false,
    placeholder: 'e.g. Business Development',
  },
  description: {
    label: 'Description',
    type: 'textarea',
    editable: true,
    required: false,
    placeholder: 'Add a short summary...',
  },
}

// Ensure all UpdateEntityAgency fields are defined with metadata
export const UPDATE_ENTITY_AGENCY_FIELDS: Record<
  keyof Omit<UpdateEntityAgency, 'entity_type'>,
  FieldMetadata
> = {
  name: {
    label: 'Name',
    type: 'text',
    editable: true,
    required: true,
    placeholder: 'e.g. General Services Administration',
  },
  short_name: {
    label: 'Short Name',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. GSA',
  },
  abbreviation: {
    label: 'Abbreviation',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. GSA',
  },
  acronym: {
    label: 'Acronym',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. GSA',
  },
  aliases: {
    label: 'Aliases',
    type: 'tag_input',
    editable: true,
    required: false,
    placeholder: 'Add alias...',
  },
  parent_entity_agency_id: {
    label: 'Parent Agency',
    type: 'hidden',
    editable: false,
    required: false,
  },
  jurisdiction_type: {
    label: 'Jurisdiction',
    type: 'dropdown',
    editable: true,
    required: false,
    placeholder: 'Select jurisdiction',
    options: ENTITY_AGENCY_JURISDICTION_TYPE_OPTIONS,
  },
  agency_type: {
    label: 'Agency Type',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select or type agency type',
    options: ENTITY_AGENCY_TYPE_OPTIONS,
  },
  agency_level: {
    label: 'Agency Level',
    type: 'text',
    editable: true,
    required: false,
    placeholder: 'e.g. sub-tier, office, division',
  },
  official_website_url: {
    label: 'Website',
    type: 'url',
    editable: true,
    required: false,
    placeholder: 'e.g. https://www.gsa.gov',
  },
  procurement_url: {
    label: 'Procurement Website',
    type: 'url',
    editable: true,
    required: false,
    placeholder: 'e.g. https://sam.gov',
  },
  vendor_registration_url: {
    label: 'Vendor Registration',
    type: 'url',
    editable: true,
    required: false,
    placeholder: 'e.g. https://example.gov/vendors',
  },
  general_email: {
    label: 'General Email',
    type: 'email',
    editable: true,
    required: false,
    placeholder: 'e.g. procurement@example.gov',
  },
  general_phone: {
    label: 'General Phone',
    type: 'phone',
    editable: true,
    required: false,
    placeholder: 'e.g. +1 (123) 456-7890',
  },
  description: {
    label: 'Description',
    type: 'textarea',
    editable: true,
    required: false,
    placeholder: 'Add a short summary...',
  },
  location_id: {
    label: 'Location',
    type: 'hidden',
    editable: false,
    required: false,
  },
  relationship_status: {
    label: 'Relationship Status',
    type: 'hidden',
    editable: false,
    required: false,
  },
  owner_user_id: {
    label: 'Owner',
    type: 'hidden',
    editable: false,
    required: false,
  },
  priority: {
    label: 'Priority',
    type: 'combobox',
    editable: true,
    required: false,
    placeholder: 'Select or type priority',
    options: ENTITY_PRIORITY_OPTIONS,
  },
  last_contacted_at: {
    label: 'Last Contacted',
    type: 'date',
    editable: true,
    required: false,
  },
  tag_ids: {
    label: 'Tags',
    type: 'tag_multi',
    editable: true,
    required: false,
  },
}

export interface EditableField<T extends Record<string, FieldMetadata>> {
  key: keyof T
  label: string
  type: FieldType
  required: boolean
  placeholder?: string
  /** Optional list of choices when using dropdown/combobox field types */
  options?: string[]
  optionDescriptions?: Record<string, string>
  optionLabels?: Record<string, string>
  optionColors?: Record<string, Color>
  allowCustom?: boolean
  dateMode?: EntityDateFieldMode
}

// Helper to get only editable fields
export const getEditableFields = <T extends Record<string, FieldMetadata>>(
  fields: T,
): EditableField<T>[] => {
  return (Object.entries(fields) as Array<[keyof T, FieldMetadata]>)
    .filter(([_, meta]) => meta.editable && meta.type !== 'hidden')
    .map(([key, meta]) => ({
      key,
      label: meta.label,
      type: meta.type,
      required: meta.required,
      placeholder: meta.placeholder,
      options: 'options' in meta ? meta.options : undefined,
      optionDescriptions:
        'optionDescriptions' in meta ? meta.optionDescriptions : undefined,
      optionLabels: 'optionLabels' in meta ? meta.optionLabels : undefined,
      optionColors: 'optionColors' in meta ? meta.optionColors : undefined,
      allowCustom: 'allowCustom' in meta ? meta.allowCustom : undefined,
      dateMode: 'dateMode' in meta ? meta.dateMode : undefined,
    }))
}

// Editable fields for merge dialogs
export const PERSON_FIELDS = getEditableFields(UPDATE_ENTITY_PERSON_FIELDS)
export type EditablePersonField = (typeof PERSON_FIELDS)[number]['key']

export const COMPANY_FIELDS = getEditableFields(UPDATE_ENTITY_COMPANY_FIELDS)
export type EditableCompanyField = (typeof COMPANY_FIELDS)[number]['key']

export const DEPARTMENT_FIELDS = getEditableFields(
  UPDATE_ENTITY_DEPARTMENT_FIELDS,
)
export type EditableDepartmentField = (typeof DEPARTMENT_FIELDS)[number]['key']

export const AGENCY_FIELDS = getEditableFields(UPDATE_ENTITY_AGENCY_FIELDS)
export type EditableAgencyField = (typeof AGENCY_FIELDS)[number]['key']

// endregion: field metadata for UI
